import {
  Activity,
  AlertTriangle,
  Biohazard,
  BusFront,
  Camera,
  CircleDot,
  CloudLightning,
  Factory,
  Flame,
  Leaf,
  MessageSquareText,
  Newspaper,
  Plane,
  Radio,
  Route,
  Satellite,
  ShieldAlert,
  Ship,
  Siren,
  Swords,
  Sun,
  Tag,
  TrainFront,
  UtilityPole,
  Wind,
  Zap,
} from "lucide-react";

import { displayText } from "./utils";

const ICON_TYPE_ALIASES = {
  abuseipdb: "cyber",
  aircraft: "flight",
  air_quality: "air-quality",
  airmet: "flight",
  airport: "flight",
  aviation: "flight",
  aviation_sigmets_airmets: "flight",
  buoy: "maritime",
  camera: "webcam",
  cdc: "health",
  cme: "space-weather",
  conflict: "conflict",
  conflicts: "conflict",
  cyber_security: "cyber",
  departures: "bus",
  disease: "outbreak",
  diseases: "outbreak",
  eia_grid_monitor: "energy",
  energy_power_plants: "factory",
  energy_substations: "utility",
  energy_transmission_lines: "utility",
  eonet: "environment",
  epidemic: "outbreak",
  epidemics: "outbreak",
  fema: "disaster",
  firms: "wildfire",
  flight: "flight",
  flights: "flight",
  gfw: "maritime",
  global_fishing_watch: "maritime",
  gs_environment: "regional-post",
  regional_post: "regional-post",
  regional_posts: "regional-post",
  posts_by_region: "regional-post",
  get_latest_environment: "regional-post",
  global_signal: "signal",
  hurricane: "weather",
  hurricanes: "weather",
  maritime: "maritime",
  nhc: "weather",
  noaa_ndbc: "maritime",
  noaa_ports: "maritime",
  noaa_tsunami: "weather",
  nws: "weather",
  openaq: "air-quality",
  opensky: "flight",
  orbit: "satellite",
  osm_healthcare: "health",
  osm_security: "security",
  osm_telecom: "telecom",
  osm_transport: "route",
  outbreak: "outbreak",
  outbreaks: "outbreak",
  pandemic: "outbreak",
  pandemics: "outbreak",
  port: "maritime",
  recall: "recall",
  recalls: "recall",
  route: "route",
  routes: "route",
  satellite: "satellite",
  satellites: "satellite",
  seismic: "seismic",
  sigmet: "flight",
  space_satellites: "satellite",
  space_weather: "space-weather",
  starlink: "satellite",
  station: "train",
  stations: "train",
  storm: "weather",
  substation: "utility",
  substations: "utility",
  transitland_departures: "bus",
  transitland_routes: "route",
  transitland_service_alerts: "alert",
  transitland_stops: "train",
  tsunami: "weather",
  urlscan: "cyber",
  waqi: "air-quality",
  webcam: "webcam",
  webcams: "webcam",
  wildfire: "wildfire",
  wildfires: "wildfire",
  windy_webcams: "webcam",
};

const markerIconRules = [
  {
    icon: Tag,
    iconType: "recall",
    terms: [
      "recall",
      "recalls",
      "product recall",
      "food recall",
      "drug recall",
      "vehicle recall",
    ],
  },
  {
    icon: Biohazard,
    iconType: "outbreak",
    terms: [
      "outbreak",
      "outbreaks",
      "disease",
      "epidemic",
      "pandemic",
      "ebola",
      "cholera",
      "measles",
      "avian flu",
      "h5n1",
    ],
  },
  {
    icon: Camera,
    iconType: "webcam",
    terms: ["windy_webcams", "webcam", "webcams", "camera", "live camera"],
  },
  {
    icon: Plane,
    iconType: "flight",
    terms: [
      "opensky",
      "aviation",
      "aircraft",
      "flight",
      "flights",
      "airport",
      "ourairports",
      "sigmet",
      "airmet",
      "aviation_sigmets_airmets",
    ],
  },
  {
    icon: Satellite,
    iconType: "satellite",
    terms: ["space_satellites", "satellite", "satellites", "orbit", "starlink"],
  },
  {
    icon: ShieldAlert,
    iconType: "cyber",
    terms: [
      "cyber",
      "abuseipdb",
      "otx",
      "urlscan",
      "ip reputation",
      "threat intel",
      "url scan",
    ],
  },
  {
    icon: Activity,
    iconType: "seismic",
    terms: [
      "usgs",
      "earthquake",
      "earthquakes",
      "seismic",
      "magnitude",
      "quake",
    ],
  },
  {
    icon: Flame,
    iconType: "wildfire",
    terms: [
      "firms",
      "wildfire",
      "wildfires",
      "fire hotspot",
      "fire",
      "thermal anomaly",
    ],
  },
  {
    icon: Wind,
    iconType: "air-quality",
    terms: ["air quality", "aqi", "openaq", "waqi", "pm2.5", "pm10"],
  },
  {
    icon: CloudLightning,
    iconType: "weather",
    terms: [
      "weather",
      "storm",
      "nws",
      "nhc",
      "hurricane",
      "tropical cyclone",
      "tsunami",
      "noaa_tsunami",
      "severe weather",
    ],
  },
  {
    icon: Sun,
    iconType: "space-weather",
    terms: [
      "space weather",
      "space_weather",
      "geomagnetic",
      "solar",
      "cme",
      "solar flare",
    ],
  },
  {
    icon: Ship,
    iconType: "maritime",
    terms: [
      "gfw",
      "global fishing watch",
      "fishing",
      "loitering",
      "ais gap",
      "maritime",
      "ship",
      "vessel",
      "noaa_ndbc",
      "noaa_ports",
      "buoy",
      "water level",
      "world_port_index",
      "port infrastructure",
    ],
  },
  {
    icon: Factory,
    iconType: "factory",
    terms: [
      "power plant",
      "energy_power_plants",
      "generation plant",
      "plant",
      "factory",
    ],
  },
  {
    icon: UtilityPole,
    iconType: "utility",
    terms: [
      "substation",
      "substations",
      "energy_substations",
      "energy_transmission_lines",
      "transmission",
      "utility",
      "power line",
    ],
  },
  {
    icon: Zap,
    iconType: "energy",
    terms: [
      "energy",
      "electric grid",
      "eia_grid_monitor",
      "grid monitor",
      "power grid",
      "electricity",
    ],
  },
  {
    icon: BusFront,
    iconType: "bus",
    terms: [
      "transitland_departures",
      "transit departure",
      "departures",
      "bus departure",
    ],
  },
  {
    icon: AlertTriangle,
    iconType: "alert",
    terms: [
      "transitland_service_alerts",
      "transit alert",
      "service alert",
      "alert",
    ],
  },
  {
    icon: Route,
    iconType: "route",
    terms: [
      "transitland_routes",
      "transit route",
      "osm_transport",
      "transport node",
      "border_control",
      "route",
    ],
  },
  {
    icon: TrainFront,
    iconType: "train",
    terms: ["transitland_stops", "transit stop", "station", "train", "rail"],
  },
  {
    icon: Radio,
    iconType: "telecom",
    terms: ["osm_telecom", "telecom", "communication tower", "radio tower"],
  },
  {
    icon: ShieldAlert,
    iconType: "security",
    terms: [
      "osm_security",
      "security sensitive",
      "military",
      "prison",
      "wikidata_embassies",
      "embassy",
      "diplomatic",
    ],
  },
  {
    icon: Activity,
    iconType: "health",
    terms: [
      "cdc",
      "health notice",
      "travel health",
      "osm_healthcare",
      "healthcare",
      "hospital",
      "clinic",
    ],
  },
  {
    icon: Leaf,
    iconType: "environment",
    terms: [
      "environment",
      "environmental",
      "eonet",
      "landslide",
      "volcano",
      "drought",
    ],
  },
  {
    icon: Siren,
    iconType: "disaster",
    terms: ["disaster", "fema", "gdacs", "declaration", "emergency"],
  },
  {
    icon: Swords,
    iconType: "conflict",
    terms: [
      "conflict",
      "conflicts",
      "conflict event",
      "armed conflict",
      "war",
      "battle",
      "clash",
      "strike",
      "attack",
      "crisiswatch",
      "crisis group",
      "gdelt conflict",
    ],
  },
  {
    icon: MessageSquareText,
    iconType: "regional-post",
    terms: [
      "regional-post",
      "regional post",
      "regional posts",
      "posts by region",
      "environment post",
      "environment posts",
      "getlatestenvironment",
      "get latest environment",
      "gs environment",
    ],
  },
  {
    icon: Newspaper,
    iconType: "news",
    terms: ["gdelt", "geopolitical", "news-derived", "news", "article"],
  },
  {
    icon: Radio,
    iconType: "signal",
    terms: ["signal", "global_signal"],
  },
];

const collectText = (value) => {
  if (value === null || value === undefined || value === "") return [];

  if (["string", "number", "boolean"].includes(typeof value)) {
    return [String(value)];
  }

  if (Array.isArray(value)) return value.flatMap(collectText);

  if (typeof value === "object") {
    return [
      value.id,
      value.type,
      value.name,
      value.label,
      value.title,
      value.category,
      value.source,
      value.sourceLabel,
      value.source_type,
      value.target_type,
    ].flatMap(collectText);
  }

  return [];
};

const markerSearchText = (properties = {}) =>
  [
    properties.id,
    properties.type,
    properties.category,
    properties.source,
    properties.sourceLabel,
    properties.postSource,
    properties.source_type,
    properties.target_type,
    properties.title,
    properties.description,
    properties.iconType,
    properties.groupIconType,
    ...(Array.isArray(properties.tags) ? properties.tags : []),
    ...collectText(properties.meta),
  ]
    .map((value) => displayText(value))
    .filter(Boolean)
    .join(" ")
    .replace(/[_-]+/g, " ")
    .toLowerCase();

export const normalizeMarkerIconType = (value) => {
  const rawType = displayText(value, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (!rawType) return "";

  return ICON_TYPE_ALIASES[rawType] || rawType;
};

const explicitIconType = (properties = {}) =>
  normalizeMarkerIconType(properties.iconType || properties.groupIconType);

export const selectMarkerPresentation = (properties = {}) => {
  const searchText = markerSearchText(properties);

  // Recalls and outbreaks should always resolve to their custom canvas icon
  // types. Do this before generic explicit handling so plural source/category
  // values like "recalls" and "outbreaks" cannot leak into Lucide rendering.
  if (searchText.includes("recall")) {
    return { icon: Tag, iconType: "recall" };
  }

  if (
    searchText.includes("outbreak") ||
    searchText.includes("disease") ||
    searchText.includes("epidemic") ||
    searchText.includes("pandemic")
  ) {
    return { icon: Biohazard, iconType: "outbreak" };
  }

  const explicit = explicitIconType(properties);

  if (explicit) {
    const explicitRule = markerIconRules.find(
      (rule) => rule.iconType === explicit,
    );

    if (explicitRule) {
      return { icon: explicitRule.icon, iconType: explicit };
    }
  }

  const matchedRule = markerIconRules.find((rule) =>
    rule.terms.some((term) => searchText.includes(String(term).toLowerCase())),
  );

  return {
    icon: matchedRule?.icon || CircleDot,
    iconType: matchedRule?.iconType || "point",
  };
};

export const selectMarkerIcon = (properties = {}) =>
  selectMarkerPresentation(properties).icon;

export const selectMarkerIconType = (properties = {}) =>
  selectMarkerPresentation(properties).iconType;

export default selectMarkerPresentation;
